import { useCallback, useEffect, useState } from 'react';

/**
 * Browser full screen for the whole page. The page itself goes full screen,
 * not one panel, so dialogs portalled into <body> stay visible. `active`
 * follows `fullscreenchange`, so leaving with Esc updates it too.
 */
export function useFullscreen() {
  const supported = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);
  const [active, setActive] = useState(() => supported && Boolean(document.fullscreenElement));

  useEffect(() => {
    if (!supported) return;
    const onChange = () => setActive(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [supported]);

  // A refused request (blocked by the browser or an iframe) changes nothing,
  // and `active` only moves on `fullscreenchange`, so the button stays truthful.
  const toggle = useCallback(() => {
    const request = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen();
    request.catch(() => undefined);
  }, []);

  const exit = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
  }, []);

  return { supported, active, toggle, exit };
}
